import { useState, useRef, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import './TagPoolInput.css';

/**
 * Tag pool input component with multi-select and custom text input
 * 
 * @param {Object} props
 * @param {string} props.label - Label for the input
 * @param {string[]} props.value - Array of currently selected values
 * @param {function} props.onChange - Called with new array when selection changes
 * @param {string[]} props.presets - Array of preset tag options
 * @param {string} props.placeholder - Placeholder for custom input
 */
export default function TagPoolInput({
  label,
  value = [],
  onChange,
  presets = [],
  placeholder = 'Add custom...',
}) {
  const [customInput, setCustomInput] = useState('');
  const inputRef = useRef(null);

  // Toggle a tag (add if not selected, remove if selected)
  const toggleTag = (tag) => {
    if (value.includes(tag)) {
      onChange(value.filter(v => v !== tag));
    } else {
      onChange([...value, tag]);
    }
  };

  // Remove a selected tag
  const removeTag = (tag) => {
    onChange(value.filter(v => v !== tag));
  };

  // Add custom tag
  const addCustomTag = () => {
    const trimmed = customInput.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setCustomInput('');
    }
  };

  // Handle Enter key in custom input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomTag();
    }
  };

  // Separate selected into preset vs custom
  const selectedPresets = value.filter(v => presets.includes(v));
  const selectedCustom = value.filter(v => !presets.includes(v));

  return (
    <div className="tag-pool-input">
      {label && <label className="tag-pool-label">{label}</label>}
      
      {/* Selected tags row */}
      {value.length > 0 && (
        <div className="tag-pool-selected">
          {value.map(tag => (
            <span 
              key={tag} 
              className={`tag-selected ${presets.includes(tag) ? 'preset' : 'custom'}`}
              onClick={() => removeTag(tag)}
            >
              {tag}
              <X size={12} />
            </span>
          ))}
        </div>
      )}

      {/* Preset tag pool */}
      <div className="tag-pool-presets">
        {presets.map(tag => (
          <button
            key={tag}
            type="button"
            className={`tag-preset ${value.includes(tag) ? 'selected' : ''}`}
            onClick={() => toggleTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Custom input */}
      <div className="tag-pool-custom">
        <input
          ref={inputRef}
          type="text"
          className="tag-pool-input-field"
          placeholder={placeholder}
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="tag-pool-add-btn"
          onClick={addCustomTag}
          disabled={!customInput.trim()}
        >
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
}

